import DomPurify from "isomorphic-dompurify";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import { getBlurUrl } from "components/Image/utils";

import { RecordItem } from "types/common";
type MasonryImage = {
  imageUrl: string;
  sizeBucket: "small" | "medium" | "large";
};

interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

type HorizontalMasonryProps = {
  bgColor?: string;
  title?: string;
  content?: string;
  ctaUrl?: string;
  ctaText?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  images: MasonryImage[];
};

const chunk = (array: MasonryImage[]) => {
  const list: Array<RecordItem> = [];
  if (array?.length > 0) {
    let images = [...array];
    // resize the images to 51 elements (3 sets of your 17 images)
    while (images.length < 51) {
      images = [...images, ...array];
    }

    // create chunks
    let i = 0,
      size = 2;
    while (i + size <= images.length) {
      list.push(images.slice(i, i + size));
      i += size;
      size = size === 5 ? 2 : size + 1;
    }
  }
  return list;
};

function renderPicture(picture: MasonryImage, { w = 1, h = 1, priority = false } = {}) {
  // Dynamically determine the sizes attribute based on the sizeBucket property
  const sizesValue =
    picture.sizeBucket === "small"
      ? "(max-width: 640px) 100px, (max-width: 1024px) 200px, 300px"
      : picture.sizeBucket === "medium"
        ? "(max-width: 640px) 200px, (max-width: 1024px) 400px, 400px"
        : "(max-width: 640px) 300px, (max-width: 1024px) 400px, 400px"; // large

  return (
    <div
      className="relative"
      style={{
        width: `${200 * w}px`,
        height: `${160 * h}px`
      }}
    >
      <Image
        src={picture.imageUrl}
        blurDataURL={getBlurUrl()}
        placeholder="blur"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}
        className="rounded-xl p-2"
        alt=""
        fill
        sizes={sizesValue}
        quality={70}
      />
    </div>
  );
}

function renderChunk(chunk: MasonryImage[], chunkIndex: number) {
  const priority = chunkIndex < 5; // first 5 chunks get priority

  if (!chunk?.length) return null;

  if (chunk.length === 2) {
    return (
      <div className="grid w-[200px] grid-cols-1 grid-rows-2">
        {renderPicture(chunk[0], { h: 1.5, priority })}
        {renderPicture(chunk[1], { h: 1.5, priority })}
      </div>
    );
  }
  if (chunk.length === 3) {
    return (
      <div className="grid w-[400px] grid-rows-[1fr_2fr]">
        <div className="grid grid-cols-2">
          {renderPicture(chunk[0], { h: 1, priority })}
          {renderPicture(chunk[1], { h: 1, priority })}
        </div>
        {renderPicture(chunk[2], { w: 2, h: 2, priority })}
      </div>
    );
  }
  if (chunk.length === 4) {
    return (
      <div className="grid w-[400px] grid-rows-[1fr_2fr]">
        {renderPicture(chunk[0], { w: 2, priority })}
        <div className="grid grid-cols-2">
          <div className="grid grid-rows-2">
            {renderPicture(chunk[1], { w: 1, h: 1, priority })}
            {renderPicture(chunk[2], { w: 1, h: 1, priority })}
          </div>
          {renderPicture(chunk[3], { w: 1, h: 2, priority })}
        </div>
      </div>
    );
  }
  if (chunk.length === 5) {
    return (
      <div className="grid w-[400px] grid-rows-3">
        <div className="grid grid-cols-2">
          {renderPicture(chunk[0], { priority })}
          {renderPicture(chunk[1], { priority })}
        </div>
        {renderPicture(chunk[2], { w: 2, priority })}
        <div className="grid grid-cols-2">
          {renderPicture(chunk[3], { priority })}
          {renderPicture(chunk[4], { priority })}
        </div>
      </div>
    );
  }
  return null;
}

const HorizontalMasonry = ({ bgColor, title, content, button1, button2, images }: HorizontalMasonryProps) => {
  const chunks = useMemo(() => {
    return chunk(images);
  }, [images]);

  if (!chunks.length) return null;

  return (
    <div className="pb-8 pt-32" id="HorizontalMasonry-67" style={{ backgroundColor: bgColor }}>
      {title && (
        <h2 className="tile-main-header text-center" dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title) }} />
      )}
      {content && <p className="text-neutral-800 mt-2 text-center text-lg">{content}</p>}
      <Link href="/gallery">
        <div className="text- relative mt-10 h-[500px] overflow-hidden lg:cursor-pointer">
          <div className="absolute left-0 top-0 flex animate-horizontal-masonry-move overflow-hidden whitespace-nowrap">
            {chunks.map((chunk, i) => (
              <div key={i} className="">
                {renderChunk(chunk as MasonryImage[], i)}
              </div>
            ))}
          </div>
        </div>
      </Link>
      {(button1 || button2) && <DualButtonCTA button1={button1} button2={button2} />}
    </div>
  );
};

export default HorizontalMasonry;
